/**
 * Todos los textos del panel del Centro, en inglés. El español (../es) tiene que tener
 * exactamente las mismas claves: lo revisa TypeScript, y la prueba `sueltos.test.ts` avisa
 * si quedó un texto escrito directo en una pantalla.
 */
const en = {
  comun: {
    cargando: 'Loading…',
    volverAIntentar: 'Try again',
    guardar: 'Save',
    guardando: 'Saving…',
    cancelar: 'Cancel',
    cerrar: 'Close',
    editar: 'Edit',
    elegir: 'Choose…',
    nombre: 'Name',
    notas: 'Notes',
    contacto: 'Contact',
    organizacion: 'Organization',
    organizaciones: 'Organizations',
    appliance: 'Appliance',
    appliances: 'Appliances',
    version: 'Version',
    estado: 'State',
    equipos: 'Devices',
    disco: 'Disk',
    ultimoContacto: 'Last seen',
    canal: 'channel',
    buscar: 'Search',
    sinDatos: 'No data',
    noReportada: 'not reported',
    errorRed: 'Could not reach the Control Center API. Check your connection.',
    errorSinMotivo: (estado: number) => `The API answered ${estado} without explaining why.`,
    errorRaro: 'Something unexpected happened. Try again.',
    hace: (cuanto: string) => `${cuanto} ago`,
    abriendo: 'Opening the Control Center…',
    canales: { estable: 'stable', beta: 'beta' } as Record<string, string>,
    roles: { admin: 'Administration', soporte: 'Support', ventas: 'Sales' } as Record<string, string>,
  },

  /** Lo que alguien hizo en el Centro, dicho para una persona (la bitácora y el tablero). */
  acciones: {
    'sesion.ingreso': 'signed in',
    'appliance.crear': 'created an appliance',
    'appliance.cambiar': 'changed an appliance',
    'appliance.borrar': 'deleted an appliance',
    'appliance.codigo': 'issued an enrollment code',
    'appliance.actualizar': 'asked for an update',
    'organizacion.crear': 'created an organization',
    'organizacion.cambiar': 'changed an organization',
    'licencia.emitir': 'issued a licence',
    'licencia.reenviar': 'sent a licence again',
    'licencia.revocar': 'revoked a licence',
    'version.publicar': 'published a version',
    'version.retirar': 'withdrew a version',
    'version.actualizar-canal': 'rolled out a version',
    'orden.cancelar': 'cancelled an order',
    'sesion.fallida': 'failed to sign in',
    'centro.consentimiento': 'changed the consent',
    'usuario.cambiar': 'changed a user',
    'usuario.crear': 'created a user',
  } as Record<string, string>,

  ajustes: {
    nav: 'Settings sections',
    grupos: { acceso: 'Clients and access', actualizaciones: 'Updates', avisos: 'Alerts' },
    secciones: {
      organizaciones: 'Organizations',
      usuarios: 'Users',
      claves: 'API keys',
      bitacora: 'Audit log',
      versiones: 'Versions',
      correo: 'Mail server',
      avisos: 'My alerts',
    },
    titulo: 'Settings',
    que: 'How the Control Center alerts reach us, and what each of us wants to hear about.',
    correo: 'Mail server',
    correoQue: 'The Control Center sends its alerts from here. It is its own server: nothing to do with what an appliance sends to its client.',
    modos: { smtp: 'SMTP server', m365: 'Microsoft 365' } as Record<string, string>,
    remitente: 'Sends as',
    remitenteEjemplo: 'centro@kuantabridge.com',
    servidor: 'Server',
    servidorEjemplo: 'smtp.office365.com',
    puerto: 'Port',
    tlsDirecto: 'Direct TLS (port 465). Otherwise STARTTLS when the server offers it.',
    usuario: 'User',
    tenant: 'Tenant',
    app: 'Application id',
    clave: 'Password',
    secreto: 'Application secret',
    yaGuardada: 'already saved',
    vacioLaDeja: 'Leave it empty to keep the one already saved.',
    noSeMuestra: 'It is stored on the server and never shown again.',
    faltaRemitente: 'Write the address it sends from.',
    faltaHost: 'Write the server address.',
    faltaTenant: 'Write the tenant.',
    faltaApp: 'Write the application id.',
    correoGuardado: 'Mail server saved.',
    probar: 'Send a test',
    paraLaPrueba: 'Send the test to',
    paraInvalido: 'Enter the address you want the test sent to.',
    pruebaMandada: (a: string) => `Test sent to ${a}.`,
    misAvisos: 'What I want to hear about',
    misAvisosQue: 'Each alert is sent once when it starts and once when it is over. Nothing repeats while it is still going.',
    idiomaDelCorreo: 'Language of my emails',
    suscripcionGuardada: 'Saved: this is what you will get.',
    porOmision: 'This is what your role gets by default.',
  },

  /** Cada aviso del Centro, dicho corto (docs/centro.md §6). */
  avisos: {
    sin_contacto: 'An appliance stops answering',
    disco: 'Its disk is filling up',
    motor_caido: 'One of its engines is down',
    licencia_por_vencer: 'A licence is about to expire',
    licencia_vencida: 'A licence expired',
    version_vieja: 'An appliance is behind on versions',
    actualizacion_fallida: 'An update failed',
    entregas_fallidas: 'A client’s alerts are not going out',
  } as Record<string, string>,

  soporte: {
    titulo: 'Support session',
    estados: { ninguna: 'none', pedida: 'waiting for the client', abierta: 'open' } as Record<string, string>,
    que: 'Reaching their devices covers what they monitor. To reach anything else on their network —a server, something nobody registered— you ask for a session and the client approves it.',
    motivo: 'What for',
    motivoEjemplo: 'Check the mail server that stopped answering',
    motivoAyuda: 'The client reads this: say what you are going to do, not “support”.',
    loPidio: 'Asked by',
    aprobo: 'Approved by',
    pedida: 'Asked',
    hasta: 'Until',
    quedan: (min: number) => (min >= 60 ? `${Math.floor(min / 60)} h ${min % 60} min left` : `${min} min left`),
    esperando: 'The client has to approve it in their own KNOC360, under Settings → Control center.',
    pedirla: 'Ask for a session',
    pedirBoton: 'Ask the client',
    pedirQue: 'It reaches the client right away. Until they approve it, nothing opens.',
    porCuanto: 'For how long',
    horas: (h: number) => (h === 1 ? '1 hour' : `${h} hours`),
    faltaMotivo: 'Say what it is for: the client reads it.',
    pedidaOk: 'Asked. The client decides now.',
    cancelar: 'Cancel it',
    cancelada: 'Cancelled.',
    abrirPuente: 'Open a bridge',
    abrirBoton: 'Get the link',
    puenteQue: 'A port on your own PC that comes out inside the client network. Your program connects to 127.0.0.1 and the opener does the rest.',
    destino: 'Address on their network',
    puerto: 'Port',
    faltaDestino: 'Say what to reach.',
    porCualMotor: 'Which engine',
    motorSolo: 'The one that monitors that network',
    motorAyuda: 'Only needed when the client has more than one and the address is not one of their devices.',
    listo: 'Ready.',
    listoQue: 'Open it with the KNOC360 opener installed on this PC:',
    abrirlo: 'Open it',
    sinContacto: 'The appliance is out of touch: it cannot be asked right now.',
  },

  codigo: {
    para: (nombre: string) => `Enrollment code for ${nombre}`,
    titulo: 'Enrollment code',
    como: 'Single use, expires in 7 days. The appliance sends it once, while installing, to get registered.',
    que: 'Copy it now and hand it to whoever installs the appliance. If it gets lost it cannot be recovered: you have to ask for a new one from the appliance detail.',
    unaSolaVez: 'This code is never shown again.',
    guardado: 'I saved it',
  },

  marco: {
    pantallas: 'Control Center screens',
    tablero: 'Dashboard',
    appliances: 'Appliances',
    red: 'Network',
    ajustes: 'Settings',
    salir: 'Sign out',
    idioma: 'Language',
  },

  ingreso: {
    marca: '· Control Center',
    lema: 'Every appliance,\non one screen.',
    que: 'The whole fleet: who is online, what version they run, how full their disks are and whose license is about to expire. This is the panel Kuanta Bridge uses inside, not the one a customer sees.',
    titulo: 'Sign in to your account',
    interno: 'KNOC360 Control Center · internal use',
    vencida: 'Your session expired. Sign in again to pick up where you left off.',
    correo: 'Email',
    correoEjemplo: 'you@kuantabridge.com',
    clave: 'Password',
    entrando: 'Signing in…',
    entrar: 'Sign in',
    olvide: 'Forgot your password?',
    olvideTitulo: 'Reset your password',
    olvideQue: 'Type your account email and we will send you a link to choose a new password.',
    mandarEnlace: 'Send the link',
    mandando: 'Sending…',
    revise: 'Check your email',
    enviado: (c: string) =>
      `If ${c} has a Control Center account, a link arrives in a couple of minutes. It works once and expires in 30 minutes. If it does not arrive, check your spam folder or ask an administrator to reset it.`,
    volverAEntrar: 'Back to sign in',
    nuevaTitulo: 'Choose a new password',
    nuevaQue: 'Saving it signs out any session opened with the old one.',
    nueva: 'New password',
    repetir: 'Repeat it',
    corta: 'The password must be at least 10 characters long.',
    noCoinciden: 'The two passwords are not the same.',
    guardando: 'Saving…',
    guardarClave: 'Save the password',
    claveLista: 'Password changed',
    claveListaQue: 'You can sign in with the new password now.',
    enlaceMuerto: 'That link no longer works',
    enlaceMuertoQue: 'It expired or was already used. Ask for a new one from the sign-in screen.',
    pie: 'Control Center · Kuanta Bridge',
  },

  tablero: {
    titulo: 'Dashboard',
    que: 'The Kuanta Bridge appliance fleet: what needs someone today, and what is running fine.',
    alDia: (hora: string) => `as of ${hora}`,
    applianceEnLinea: 'Appliances online',
    sinContactoN: (n: number) => `${n} out of touch`,
    esperandoAltaN: (n: number) => `${n} waiting for enrollment`,
    suspendidosN: (n: number) => `${n} suspended`,
    equiposMonitoreados: 'Devices monitored',
    equiposCaidosN: (n: string) => `${n} down right now`,
    todosArriba: 'all of them answering',
    licenciasActivas: 'Licences in force',
    porVencerN: (n: number) => `${n} expiring`,
    vencidasN: (n: number) => `${n} expired`,
    sinLicenciaN: (n: number) => `${n} without one`,
    todasAlDia: 'nothing to renew soon',
    ultimaEstable: 'Latest stable version',
    atrasadosN: (n: number) => `${n} appliance(s) behind`,
    todosAlDiaVersion: 'the whole fleet is on it',
    sinVersiones: 'no stable version published yet',
    radar: 'The fleet, live',
    radarQue:
      'Each blip is an appliance: the further from the centre, the longer since its last heartbeat. Colour is how it is doing; each wedge is a client.',
    paraMirar: 'Needs someone today',
    todoEnOrden: 'Nothing needs attention',
    todoEnOrdenQue: (appliances: string, equipos: string) =>
      `${appliances} appliance(s) online, with healthy disks and their engines connected, watching ${equipos} devices.`,
    renovaciones: 'Renewals coming up',
    sinRenovaciones: 'No licence expires in the next 60 days.',
    enDias: (d: number) => (d === 0 ? 'today' : `in ${d} d`),
    vencidaHace: (d: number) => `${d} d ago`,
    porVersion: 'Fleet by version',
    sinVersion: 'No appliance has reported its version yet.',
    clientes: 'Clients',
    sinClientes: 'No organization has appliances yet.',
    salud: 'Devices up',
    compartida: 'network shared',
    actividad: 'Latest activity',
    sinActividad: 'Nothing logged yet.',
  },

  consola: {
    titulo: (nombre: string) => `Console of ${nombre}`,
    que: 'SSH to the appliance itself, through its channel with the Control Center: no VPN and no open port at the client. As soporte it signs in with the Control Center key; older appliances need their password, which is not stored.',
    conLlave: 'Empty: Control Center key',
    usuario: 'User',
    clave: 'Password',
    entrar: 'Connect',
    fallo: 'The console could not open.',
    estados: {
      datos: '',
      conectando: 'Reaching the appliance…',
      entrando: 'Signing in…',
      abierta: 'Connected',
      cerrada: 'Closed',
    } as Record<string, string>,
    otraVez: 'Connect again',
    cerrar: 'Disconnect',
    sesionCerrada: 'The session was closed.',
  },
  libres: {
    titulo: 'Waiting for an organization',
    que: 'These appliances registered by themselves as soon as they had Internet access. Recognise each one by the fingerprint on its screen, then assign it to an organization.',
    huella: 'Fingerprint',
    ip: 'Connects from',
    version: 'Version',
    visto: 'Last seen',
    enLinea: 'Online',
    sinContacto: 'No contact',
    consola: 'Console',
    asignar: 'Assign',
    asignarTitulo: (nombre: string) => `Assign ${nombre}`,
    asignarQue: 'From now on it is one more appliance of that organization: it shows it on its screen and in its web.',
    nombreAyuda: 'How it will be seen in the Control Center.',
    descartar: 'Discard',
    descartarTitulo: (nombre: string) => `Discard ${nombre}?`,
    descartarQue:
      'It is disconnected and cannot register again by itself with this key. Use it for a test or a cloned VM, not for a client appliance.',
    asignado: (nombre: string, organizacion: string) => `${nombre} now belongs to ${organizacion}.`,
    descartado: (nombre: string) => `${nombre} was discarded.`,
    faltaOrganizacion: 'Choose the organization.',
  },
  appliances: {
    titulo: 'Appliances',
    que: 'Everything Kuanta Bridge has installed. The Control Center knows how each one is doing, never what it monitors.',
    nuevo: 'New appliance',
    crearYCodigo: 'Create and get the code',
    elijaOrganizacion: 'Choose the organization that bought it.',
    faltaNombre: 'Give the appliance a name.',
    sinOrganizaciones: 'There are no organizations yet: create one first.',
    nombreEjemplo: 'Hotel Norte appliance',
    nombreAyuda: 'This is how it shows up here and in its own web.',
    notasOpcional: 'Notes (optional)',
    notasEjemplo: 'Where it sits, who installed it, anything worth knowing.',
    todasLasOrganizaciones: 'All organizations',
    estado: 'Status',
    todosLosEstados: 'All statuses',
    activo: 'Active',
    esperandoAlta: 'Waiting for enrollment',
    suspendido: 'Suspended',
    revocado: 'Revoked',
    buscarEjemplo: 'Search by name or organization',
    nadaConEsoFiltros: 'No appliance matches what you searched for. Try with fewer filters.',
    ningunoTodavia: 'No appliances in the Control Center yet. Create the first one with “New appliance”.',
    caidos: 'Down',
    creado: (nombre: string) => `${nombre} created.`,
  },

  appliance: {
    volver: '← Back to appliances',
    detalles: 'Details',
    abrirSuApp: 'Open their KNOC360',
    codigoNuevo: 'New code',
    activar: 'Activate',
    suspender: 'Suspend',
    revocar: 'Revoke',
    borrar: 'Delete',
    sistema: 'Operating system',
    prendidoDesde: 'Up since',
    noReportadaTodavia: 'not reported yet',
    nuncaConecto: 'never connected',
    pestanas: 'Appliance sections',
    pestana: {
      salud: 'Health',
      actualizaciones: 'Updates',
      internet: 'Internet access',
      licencia: 'Licence',
      soporte: 'Support',
      consola: 'Console',
      detalles: 'Details',
    },
    canalEstableQue: 'Stable: only versions that were already tested are offered. The right choice for a client in production.',
    canalBetaQue: 'Beta: gets new versions first, to try them before they go to stable. For test or trusted appliances.',
    canalDeVersiones: 'Update channel',
    canalCambiado: 'Update channel changed.',
    creadoEnElCentro: 'Created in the Control Center',
    notasGuardadas: 'Notes saved.',
    sinNotas: 'No notes.',
    direccionInternet: 'Enter from the internet',
    sinNombreInternet: 'No name: its people can only enter from inside their network.',
    nombreInternet: 'Name',
    nombreEjemplo: 'e.g. ews',
    conNombre: 'Named',
    sinNombre: 'No name',
    guardarNombre: 'Save',
    quitarNombre: 'Remove',
    nombreGuardado:
      'Name saved. The appliance already knows its address; its administrator has to turn on entering from the internet in their KNOC360.',
    nombreQuitado: 'The name was removed: it can no longer be entered from the internet.',
    nombreAyuda: 'One name per appliance: the login decides which client each person sees. DNS and the certificate for the name are separate.',
    nombresApagados: 'This Control Center has no domain set for names.',
    descartar: 'Discard',
    guardarNotas: 'Save notes',
    saludAhora: 'Health now',
    noSeDioDeAlta: 'This appliance has not enrolled yet: it never reported health.',
    sinSalud: 'No health has arrived from this appliance yet.',
    cpu: 'CPU',
    memoria: 'Memory',
    libres: (gb: string) => `${gb} GB free`,
    base: 'Database',
    prendidoHace: (cuanto: string) => `up ${cuanto}`,
    equiposCaidos: (n: string) => `${n} down`,
    motores: 'Engines',
    conectados: 'connected',
    licenciaVence: 'expires',
    licenciaTope: 'cap',
    licenciaEquipos: 'devices',
    ultimas24: 'Last 24 hours',
    ultimosErrores: 'Last errors it reported',
    sinErrores: 'It reported no errors in the last day.',
    activado: (nombre: string) => `${nombre} is active again.`,
    suspenderA: (nombre: string) => `Suspend ${nombre}?`,
    suspendido: (nombre: string) => `${nombre} is suspended.`,
    suspenderQue:
      "It gets marked as suspended here and stops counting as active. The appliance keeps monitoring and alerting on the customer's network: this does not turn it off. You can activate it again whenever you want.",
    revocarA: (nombre: string) => `Revoke the credential of ${nombre}?`,
    revocarBoton: 'Revoke the credential',
    revocado: (nombre: string) => `Credential of ${nombre} revoked.`,
    revocarCorta: 'Its control channel gets cut.',
    revocarQue:
      'The connection drops right now and never opens again: the Control Center stops receiving its health and cannot send it any more orders or licenses.',
    revocarSigue:
      'The appliance keeps monitoring, alerting and backing up on the customer’s network, and says on its own web that it lost contact with the Control Center. To bring it back you have to enroll it again with a new code.',
    borrarA: (nombre: string) => `Delete ${nombre} from the Control Center?`,
    borrarQue:
      'This appliance never connected, so it can be deleted without leaving anything dangling: it goes away with its enrollment code. If it was already installed somewhere, cancel and revoke it instead.',
  },

  licencia: {
    titulo: 'Licence',
    sinLicencia: 'This appliance has no licence yet. It keeps working, but nobody is paying for it.',
    plan: 'Plan',
    planes: { basico: 'Basic', pro: 'Pro', ilimitado: 'Unlimited' } as Record<string, string>,
    sinTope: 'no device limit',
    equiposCortos: 'devices',
    equipos: (n: string) => `${n} devices`,
    vence: 'Expires',
    emitida: 'Issued',
    entregada: 'Delivered to the appliance',
    todaviaNo: 'not yet',
    esperandoAlAppliance: 'waiting: the appliance is offline',
    venceHoy: 'expires today',
    faltan: (d: number) => `${d} day${d === 1 ? '' : 's'} left`,
    vencioHace: (d: number) => `expired ${d} day${d === 1 ? '' : 's'} ago`,
    emitir: 'Issue licence',
    renovar: 'Renew',
    reenviar: 'Send again',
    bajarArchivo: 'Download file',
    revocar: 'Revoke',
    mandada: 'Licence sent to the appliance.',
    mandadaDespues: 'The appliance is offline: it will get it when it connects.',
    anteriores: 'Earlier licences',
    hasta: 'Expiry',
    emitidaPor: 'Issued by',
    reemplazada: 'replaced',
    revocadaPor: (motivo: string) => `revoked: ${motivo}`,
    renovarTitulo: 'Renew licence',
    faltaEquipos: 'Say how many devices the licence covers.',
    faltaVence: 'Say when it expires.',
    emitidaOk: 'Licence issued.',
    ilimitado: 'Unlimited (no device limit)',
    equiposQueCubre: 'Devices covered',
    venceEl: 'Expires on',
    queImplica:
      'Going over the device limit stops new devices from being added; the ones already there keep being monitored. After it expires there are 30 more days, and then the appliance goes read-only — it never stops monitoring.',
    revocarTitulo: 'Revoke this licence',
    faltaMotivo: 'Say why: it stays in the log.',
    revocada: 'Licence revoked.',
    revocarQue: 'The appliance goes back to whatever licence it had before this one, or to none. It keeps monitoring either way.',
    porQue: 'Why',
    porQueEjemplo: 'Issued by mistake, contract cancelled…',
    estados: {
      vigente: 'License valid',
      por_vencer: 'License expiring soon',
      gracia: 'License in grace period',
      solo_lectura: 'License expired (read-only)',
      sin_licencia: 'No license',
    } as Record<string, string>,
  },

  actualizar: {
    hayNueva: (v: string) => `New version available: ${v}`,
    actualizarA: (v: string) => `Update to ${v}`,
    queTrae: 'What is new',
    otraVersion: 'Other version…',
    esperandoAvance: 'the appliance got it…',
    titulo: 'Updates',
    enLaVersion: (v: string) => `on ${v}`,
    versionNoReportada: 'version not reported',
    ventana: 'Maintenance window',
    cualquierDia: 'any day',
    horaDelAppliance: (cuando: string) => `${cuando} (appliance time)`,
    sinVentana: 'none: it updates as soon as it is asked',
    actualizarEste: 'Update this appliance',
    cambiarVentana: 'Change the window',
    yaHayUna: 'There is already an update in progress.',
    sinVersionesNuevas: 'No newer version with a package is published.',
    alDia: (v: string) => `Up to date: ${v} is the newest version.`,
    nadaPedido: 'Nothing has been asked of this appliance yet.',
    loPedido: 'What has been asked',
    cuando: 'Asked',
    que: 'What',
    comoFue: 'How it went',
    ordenCancelada: 'Order cancelled.',
    pedirBoton: 'Ask it to update',
    elijaVersion: 'Choose the version.',
    pedida: 'Update asked for. It will be applied inside its maintenance window.',
    queHace:
      'The appliance waits for its maintenance window, checks the package signature, backs up its database and master key, updates, and rolls back on its own if it ends up unhealthy. Monitoring is not interrupted while it waits.',
    ventanaGuardada: 'Maintenance window saved.',
    mismaHora: 'Start and end cannot be the same time.',
    ventanaQue: 'When this appliance is allowed to update itself, in its own local time. No days chosen means any day.',
    dias: 'Days',
    desde: 'From',
    hasta: 'To',
    quitarVentana: 'Remove the window',
    diasCortos: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    estados: { hecha: 'done', entregada: 'in progress', pendiente: 'waiting for the appliance', fallo: 'failed' } as Record<string, string>,
  },

  versiones: {
    titulo: 'Versions',
    que: 'What has been released and what the fleet is running. Publishing does not touch any appliance: updating is asked for separately.',
    publicar: 'Publish a version',
    listas: 'Ready to publish',
    listasQue: 'Packages that are already in the blob, built and signed, and are not in the catalogue yet.',
    paqueteDe: (size: string, when: string) => `${size} package · built ${when}`,
    publicarEsta: (v: string) => `Publish ${v}`,
    publicarTitulo: (v: string) => `Publish version ${v}`,
    canalBeta: 'Beta: test appliances get it first.',
    canalEstable: 'Stable: every appliance on that channel gets it.',
    afuera: 'The package is somewhere else (advanced): enter the address and signature by hand',
    faltaPaquete: 'The package address is missing.',
    notasDelPaquete: 'The notes come with the package: review them and publish.',
    parque: 'The fleet right now',
    sinParque: 'No appliance has reported its version yet.',
    catalogo: 'Catalogue',
    sinVersiones: 'No versions published yet.',
    numero: 'Version',
    canal: 'Channel',
    publicada: 'Published',
    notas: 'Notes',
    necesita: (v: string) => `needs ${v}+`,
    sinPaquete: 'no package: cannot be applied',
    retirada: 'withdrawn',
    // Los formatos no se traducen: OVA, VHDX y qcow2 se llaman igual en todos lados.
    imagen: 'VM image',
    sinImagen: '—',
    imagenDel: (f: string) => `Image built on ${f}`,
    formatos: { ova: 'OVA', vhdx: 'VHDX', qcow2: 'qcow2' } as Record<string, string>,
    repartir: 'Roll out',
    retirar: 'Withdraw',
    borrar: 'Delete',
    armar: 'Build VM image',
    rearmar: 'Rebuild VM image',
    armando: (paso: string) => `Building the image: ${paso}… (about 25 min)`,
    armandoOk: (v: string) => `The ${v} image is being built: it can be downloaded in about 25 minutes.`,
    armadoFallo: (m: string) => `The last build failed: ${m}`,
    borrarTitulo: (v: string) => `Delete version ${v}?`,
    borrarQue: 'It leaves the catalogue and its package and VM images (OVA, VHDX, qcow2) are deleted from the blob. This cannot be undone.',
    borradaOk: (v: string) => `Version ${v} was deleted.`,
    noBorrarEnUso: (n: number): string => (n === 1 ? '1 appliance runs it: it cannot be deleted' : `${n} appliances run it: it cannot be deleted`),
    noBorrarNueva: 'It is the newest, the one downloaded to install a new client: publish another before deleting it',
    retirarTitulo: (v: string) => `Withdraw version ${v}?`,
    retirarQue: 'It is no longer offered for updates. Appliances that already have it keep running it.',
    enAppliances: (n: number) => (n === 1 ? 'on 1 appliance' : `on ${n} appliances`),
    laTienen: (n: number): string => (n === 1 ? 'appliance has it' : 'appliances have it'),
    verTodas: (n: number) => `Show all ${n}`,
    verMenos: 'Show fewer',
    retiradaOk: (v: string) => `Version ${v} withdrawn.`,
    publicadaOk: (v: string) => `Version ${v} published.`,
    repartirTitulo: (v: string) => `Roll out ${v}`,
    repartirBoton: 'Ask every appliance in the channel',
    repartido: (pedidas: number, saltadas: number) =>
      `${pedidas} appliance(s) asked to update; ${saltadas} skipped (already on it, or the jump is too big).`,
    repartirQue: (canal: string, version: string) =>
      `Every ${canal} appliance that is not already on ${version} gets the order. Each one applies it inside its own maintenance window, checks the package signature, and rolls back on its own if it ends up unhealthy.`,
    repartirNota: 'One at a time is also possible, from each appliance’s page.',
    formatoNumero: 'The version number looks like 1.4.2.',
    faltaNotasEn: 'Write what changed, in English.',
    faltaNotasEs: 'Write what changed, in Spanish: the client reads it.',
    numeroEjemplo: '1.4.2',
    queCambioEn: 'What changed (English)',
    queCambioEs: 'What changed (Spanish)',
    paquete: 'Package URL',
    paqueteAyuda: 'Read-only link. Without it the version is only recorded: nothing can be rolled out.',
    firma: 'Package signature',
    firmaAyuda: 'The appliance checks it before installing anything.',
    minima: 'Minimum version (optional)',
    minimaAyuda: 'The oldest one that can update straight to this.',
    minimaEjemplo: '1.3.0',
  },

  red: {
    columnas: { equipo: 'Device', cliente: 'Client', estado: 'Status', queFalla: 'What fails', direccion: 'Address', appliance: 'Appliance' },
    titulo: 'Network',
    que: 'What the appliances that gave consent are monitoring. Down first, because that is what needs someone.',
    sinConsentimiento: 'No client has given consent yet. Ask them to turn it on in Settings → Control center.',
    verYOperar: 'View and operate',
    soloVer: 'View only',
    caidos: 'down ·',
    parciales: 'partial ·',
    equipos: 'devices',
    fotoVieja: (cuando: string) => `Snapshot from ${cuando} — the appliance is out of touch`,
    actualizado: (cuando: string) => `Updated ${cuando}`,
    buscarEjemplo: 'Search by name or address',
    buscarEquipos: 'Search devices',
    todaOrganizacion: 'Every organization',
    necesitanAlguien: 'Needs attention',
    cuenta: (equipos: string, atencion: string) => `${equipos} devices · ${atencion} need attention`,
    nadaCaido: 'Nothing is down right now across every client.',
    nadaCoincide: 'No device matches. Try another organization or clear the search.',
    equipo: 'Device',
    direccion: 'Address',
    cliente: 'Client',
    sitio: 'Site',
    desde: 'Since',
    reconocida: 'acknowledged',
    volverAlAppliance: '← Back to the appliance',
    // tablero de la red
    kpi: {
      disponibilidad: 'Availability',
      disponibilidadQue: (arriba: string, medibles: string) => `${arriba} of ${medibles} measurable devices up`,
      caidos: 'Down',
      parciales: 'Partial',
      sinDatos: 'No data',
      nuevas: 'New outages',
      nuevasQue: 'in the last hour',
      cronicas: 'Chronic',
      cronicasQue: 'down for over 24 h',
    },
    porCliente: 'Health by client',
    porClienteQue: 'Click a client to see only its devices.',
    antiguedad: 'How long they have been down',
    antiguedadQue: 'New outages need someone now; chronic ones need a decision.',
    queFalla: 'What is failing',
    queFallaQue: 'Checks that brought devices down or partial.',
    tramos: ['< 15 min', '15–60 min', '1–6 h', '6–24 h', '1–3 d', '> 3 d'] as string[],
    todoArriba: 'all up',
    filtrandoPor: 'Filtered by',
    quitarFiltro: 'Clear',
    estados: { caidos: 'Needs attention', todos: 'All', arriba: 'Up', desconocido: 'No data' } as Record<string, string>,
    estadoEquipo: { caido: 'Down', parcial: 'Partial', arriba: 'Up', desconocido: 'No data' } as Record<string, string>,
    fuentes: 'Sources',
    nadaQueGraficar: 'Nothing is failing: no outages to chart.',
    sinContacto: 'Out of touch',
    estasAdentro: 'You are inside the client’s own KNOC360. Everything you do is written to their audit log',
    ultimaVez: (cuando: string) => ` · last seen ${cuando}`,
    noDejaVer: 'This client has not allowed the Control Center to see their network.',
    sinContactoAhora: 'The appliance is out of touch: its network cannot be opened right now.',
    marco: (organizacion: string) => `${organizacion} · KNOC360`,
    cliente_: 'Client',
    nEquipos: (n: number) => `${n} device${n === 1 ? '' : 's'}`,
    nCaidos: (n: number) => `${n} down`,
  },

  bitacora: {
    titulo: 'Audit log',
    que: 'Who did what in the Control Center, and when. In Costa Rica time.',
    accion: 'Action',
    todasLasAcciones: 'All actions',
    nadaConEsaAccion: 'Nothing with that action.',
    nadaTodavia: 'Nothing logged yet.',
    cuando: 'When',
    quien: 'Who',
    detalle: 'Details',
  },

  clavesApi: {
    titulo: 'API keys',
    que: 'How an outside system enters this Control Center without a person: a key is worth a user of its role, in the API and on the NOC gateway to every appliance. What it can do on each client network is still what that client allowed in their appliance.',
    soloAdmin: 'Only an administrator manages API keys.',
    nueva: 'New API key',
    crear: 'Create',
    nombreEjemplo: 'Acme integration',
    nombreQue: 'Who or what uses it: that is how it shows in the audit log.',
    faltaNombre: 'The key needs a name.',
    rol: 'Role',
    queRol: {
      admin: 'Everything an administrator does, except managing API keys and nothing with a human sign-in.',
      soporte: 'Operates: enters client networks, reaches their devices, suspends appliances and reads the audit log.',
    } as Record<string, string>,
    vence: 'Expires',
    venceQue: 'Optional. After that date the key stops working on its own.',
    venceFutura: 'The expiry date has to be in the future.',
    noVence: 'never',
    estado: 'Status',
    activa: 'Active',
    apagada: 'Off',
    vencida: 'Expired',
    prefijo: 'Key',
    ultimoUso: 'Last used',
    nunca: 'never',
    creada: 'Created',
    claveDe: (n: string) => `API key for ${n}`,
    clave: 'API key',
    unaSolaVez: 'Shown only once.',
    claveQue: 'Hand it over through a secure channel. If it is lost, delete this one and create another.',
    comoSeUsa: 'It goes in every request as a bearer token. On the NOC gateway (noc.knoc360.com) the appliance is chosen with a header:',
    claveGuardada: 'I copied it',
    apagar: 'Turn off',
    encender: 'Turn on',
    borrar: 'Delete',
    apagarA: (n: string) => `Turn off ${n}`,
    apagarQue: 'Whoever uses it is cut off at once. It can be turned on again.',
    borrarA: (n: string) => `Delete ${n}`,
    borrarQue: 'It is gone for good: whoever uses it will need a new key. The audit log keeps what it did.',
    ninguna: 'No API keys yet.',
    creadaOk: (n: string) => `Created ${n}.`,
    encendida: (n: string) => `${n} is on.`,
    apagadaOk: (n: string) => `${n} is off.`,
    borradaOk: (n: string) => `Deleted ${n}.`,
  },
  usuarios: {
    titulo: 'Users',
    que: 'Who enters this Control Center and with what role. Nobody is deleted: they are deactivated, and the audit log keeps saying who did what.',
    soloAdmin: 'Only an administrator manages users.',
    nuevo: 'New user',
    crear: 'Create',
    editarA: (n: string) => `Edit ${n}`,
    correo: 'Email',
    rol: 'Role',
    estado: 'Status',
    ultimoIngreso: 'Last sign-in',
    nunca: 'never',
    vos: 'you',
    activo: 'Active',
    inactivo: 'Inactive',
    bloqueadoHasta: (c: string) => `Locked until ${c}`,
    nombreEjemplo: 'First and last name',
    correoEjemplo: 'person@kuantabridge.com',
    faltaNombre: 'The name is missing.',
    faltaCorreo: 'That email is not valid.',
    queRol: {
      admin: 'Everything, including users, revoking and deleting appliances.',
      soporte: 'Operates: opens client networks, suspends appliances and sees the audit log.',
      ventas: 'Creates organizations and appliances, and issues licenses. Does not operate networks.',
    },
    claveNueva: 'New password',
    claveNuevaPara: (n: string) => `New password for ${n}`,
    claveNuevaQue:
      'A temporary password is generated. The current one stops working and their open sessions are closed. If they were locked, they are unlocked.',
    claveDe: (c: string) => `Temporary password for ${c}`,
    claveTemporal: 'Temporary password',
    unaSolaVez: 'Shown only once.',
    claveQue: 'Hand it over through a safe channel; the person changes it from their menu after signing in.',
    claveGuardada: 'I copied it',
    desbloquear: 'Unlock',
    desactivar: 'Deactivate',
    activar: 'Activate',
    desactivarA: (n: string) => `Deactivate ${n}`,
    desactivarQue:
      'They will not be able to sign in, and if they are inside they are logged out right away. They can be activated again at any time.',
    ninguno: 'There are no users yet.',
    creado: (n: string) => `${n} was created.`,
    guardado: (n: string) => `${n} was saved.`,
    desactivado: (n: string) => `${n} was deactivated.`,
    activado: (n: string) => `${n} was activated.`,
    desbloqueado: (n: string) => `${n} was unlocked.`,
  },

  organizaciones: {
    titulo: 'Organizations',
    que: 'Who bought each appliance. An appliance always belongs to an organization.',
    nueva: 'New organization',
    editarA: (nombre: string) => `Edit ${nombre}`,
    faltaNombre: 'Give the organization a name.',
    nombreEjemplo: 'Hotel Norte',
    contactoEjemplo: 'Name, email or phone of whoever answers',
    notasEjemplo: 'What they bought, who decides, anything worth remembering.',
    ninguna: 'No organizations yet. Create the first one so you can enroll appliances.',
    creada: (n: string) => `${n} created.`,
    guardada: (n: string) => `${n} saved.`,
  },

  salud: {
    titulo: (horas: number) => `CPU, memory and disk over the last ${horas} hours`,
    sinDatos: (horas: number) => `No health reported in the last ${horas} hours.`,
    pie: 'percent, every 5 min',
  },

  estado: {
    esperandoAlta: 'Waiting for enrollment',
    sinContacto: (cuanto: string) => `No contact for ${cuanto}`,
    nuncaConecto: 'Never connected',
    discoAl: (pc: number) => `Disk at ${pc} %`,
    motoresCaidos: (caidos: number, total: number) => `${caidos} of ${total} engines down`,
    enLinea: 'Online',
  },

  noExiste: {
    titulo: 'This page does not exist',
    que: 'The link may be old or copied wrong.',
    alTablero: 'Go to the dashboard',
  },
};

export default en;
